<template>
  <div>
    <div class="grid grid-cols-2 gap-4 my-3">
      <SearchSelector
        v-if="!updating"
        id="category"
        :label="t('insurance_product')"
        name="category"
        required
        v-model="category"
        :options="categoriesSorted"
        :search-keyword-threshold="0"
      />
      <FormField
        id="storeFee"
        :label="t('merchant_commission_rate')"
        name="storeFee"
        trailing-add-ons="%"
        required
        v-model="storeFee"
      />
    </div>
    <div class="grid grid-cols-2 gap-4 my-3">
      <Selector
        id="saleFormType"
        :label="t('sale_form_type')"
        name="saleFormType"
        required
        v-model="saleFormType"
        :options="saleFormTypeOptions"
      />
    </div>
    <div class="border-t border-gray-200 pt-4 mt-4">
      <h3 class="text-sm font-medium text-gray-700 mb-3">{{ t("third_party_actors_section") }}</h3>
      <div v-for="(entry, index) in thirdPartyActorFees" :key="index" class="grid gap-4 mb-3 items-end">
        <SearchSelector
          :id="`thirdPartyActor-${index}`"
          :label="t('third_party_actor')"
          :name="`thirdPartyActor-${index}`"
          v-model="entry.thirdPartyActorId"
          :options="thirdPartyActorOptionsFor(index)"
          :search-keyword-threshold="0"
        />
        <FormField
          :id="`thirdPartyActorFee-${index}`"
          :label="t('third_party_actor_fee')"
          :name="`thirdPartyActorFee-${index}`"
          trailing-add-ons="%"
          required
          v-model="entry.fee"
        />
        <button
          type="button"
          class="mb-1.5 p-1.5 rounded-sm cursor-pointer text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          :aria-label="t('remove_third_party_actor')"
          @click="removeThirdPartyActor(index)"
        >
          <TrashIcon class="h-5 w-5" />
        </button>
      </div>
      <SecondaryButton :disabled="!canAddThirdPartyActor" @click="addThirdPartyActor">
        <PlusIcon class="h-4 w-4 mr-1" />
        {{ t("add_third_party_actor") }}
      </SecondaryButton>
    </div>
    <div class="flex justify-end">
      <SecondaryButton @click="submit">{{ t("save") }}</SecondaryButton>
    </div>
  </div>
</template>

<script>
import { Selector, SearchSelector, FormField, SecondaryButton } from "@estaly/ui";
import { TrashIcon, PlusIcon } from "@heroicons/vue/solid";
import { mapActions } from "vuex";
import { useI18n } from "vue-i18n";

export default {
  components: {
    SecondaryButton,
    Selector,
    SearchSelector,
    FormField,
    TrashIcon,
    PlusIcon,
  },
  props: {
    store: {
      type: Object,
      required: true,
    },
    businessVertical: {
      type: Object,
      required: false,
    },
  },
  emits: ["success"],
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      category: "",
      storeFee: this.businessVertical?.storeFee ? this.businessVertical.storeFee * 100 : 30,
      saleFormType: this.businessVertical?.saleFormType || "sale",
      thirdPartyActorFees:
        this.businessVertical?.thirdPartyActorFees?.map((f) => ({
          thirdPartyActorId: f.thirdPartyActorId,
          fee: f.fee * 100,
        })) || [],
      categories: [],
      thirdPartyActors: [],
      saleFormTypeOptions: [
        { label: this.t("sale"), value: "sale" },
        { label: this.t("lead"), value: "lead" },
        { label: this.t("sale_without_birth_info"), value: "sale_without_birth_info" },
      ],
    };
  },
  computed: {
    updating() {
      return !!this.businessVertical;
    },
    categoriesSorted() {
      const options = this.categories.map((category) => ({
        ...category,
        label:
          this.t(category.label) === category.label ? category.label : `${category.label} (${this.t(category.label)})`,
      }));
      return options.sort((a, b) =>
        a.value === "other_or_multiple" ? 1 : b.value === "other_or_multiple" ? -1 : a.label.localeCompare(b.label),
      );
    },
    canAddThirdPartyActor() {
      return this.thirdPartyActorFees.length < this.thirdPartyActors.length;
    },
  },
  async mounted() {
    await this.loadFormOptions();
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("businessVerticals", {
      createBusinessVertical: "create",
      updateBusinessVertical: "update",
      fetchNewOptions: "new",
    }),
    ...mapActions("thirdPartyActors", {
      fetchThirdPartyActors: "fetchAll",
    }),
    submit() {
      this.updating ? this.update() : this.create();
    },
    buildThirdPartyActorParams() {
      return {
        thirdPartyActorFees: this.thirdPartyActorFees
          .filter((f) => f.thirdPartyActorId)
          .map((f) => ({
            thirdPartyActorId: f.thirdPartyActorId,
            fee: parseFloat(String(f.fee).replace(",", ".")) / 100,
          })),
      };
    },
    thirdPartyActorOptionsFor(index) {
      const selectedIds = this.thirdPartyActorFees
        .filter((_, i) => i !== index)
        .map((f) => f.thirdPartyActorId)
        .filter(Boolean);
      return this.thirdPartyActors
        .filter((actor) => !selectedIds.includes(actor.id))
        .map((actor) => ({ label: actor.name, value: actor.id }));
    },
    addThirdPartyActor() {
      this.thirdPartyActorFees.push({ thirdPartyActorId: "", fee: "" });
    },
    removeThirdPartyActor(index) {
      this.thirdPartyActorFees.splice(index, 1);
    },
    async create() {
      const { success } = await this.createBusinessVertical({
        storeId: this.store.id,
        data: {
          category: this.category,
          storeFee: parseFloat(String(this.storeFee).replace(",", ".")) / 100,
          saleFormType: this.saleFormType,
          ...this.buildThirdPartyActorParams(),
        },
      });
      if (!success) return;

      await this.notify({
        category: "simple",
        type: "success",
        title: this.t("vertical_added"),
      });
      this.$emit("success");
    },
    async update() {
      const { success } = await this.updateBusinessVertical({
        businessVerticalId: this.businessVertical.id,
        data: {
          storeFee: parseFloat(String(this.storeFee).replace(",", ".")) / 100,
          saleFormType: this.saleFormType,
          ...this.buildThirdPartyActorParams(),
        },
      });
      if (!success) return;

      await this.notify({
        category: "simple",
        type: "success",
        title: this.t("vertical_updated"),
      });
      this.$emit("success");
    },
    async loadFormOptions() {
      const [formOptionsResponse] = await Promise.all([
        this.fetchNewOptions({ storeId: this.store.id }),
        this.loadThirdPartyActors(),
      ]);
      if (!formOptionsResponse?.success) return;

      this.categories = formOptionsResponse.data.categories;
    },
    async loadThirdPartyActors() {
      const response = await this.fetchThirdPartyActors();
      if (!response?.success) return;

      this.thirdPartyActors = response.data.records;
    },
  },
};
</script>

<i18n src="./BusinessVerticalForm.i18n.json"></i18n>
